"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var EventEmitter = require("eventemitter3");
var token_1 = require("./token");
var logger_1 = require("./logger");
var constants_1 = require("./constants");
var utils_1 = require("./utils");
/**
 * Javascript client for BlackArrow message hub.
 * In order to prevent multiple connection from being created it uses singleton pattern.
 * All messages are sent via the same ws connection
 *
 * @doc API
 */
var BlackArrowClient = /** @class */ (function () {
    function BlackArrowClient() {
        var _this = this;
        /**
         * Adds an event listener
         * @param eventName
         * @param handler
         * @throws
         */
        this.on = function (eventName, handler) {
            var eventNames = utils_1.getObjectValues(constants_1.WEB_SOCKET_EVENTS);
            if (eventNames.includes(eventName)) {
                _this.emitter.on(eventName, handler);
                return _this;
            }
            throw new Error(constants_1.INVALID_EVENT_NAME_MESSAGE);
        };
        /**
         * Removes an event listener
         * @param eventName
         * @param handler
         */
        this.off = function (eventName, handler) {
            _this.emitter.off(eventName, handler);
            return _this;
        };
        this.openWebSocket = function () {
            var url = _this.getWebSocketUrl();
            var socket = new WebSocket(url);
            socket.addEventListener(constants_1.WEB_SOCKET_EVENTS.OPEN, _this.handleOpen);
            socket.addEventListener(constants_1.WEB_SOCKET_EVENTS.CLOSE, _this.handleClose);
            socket.addEventListener(constants_1.WEB_SOCKET_EVENTS.MESSAGE, _this.handleMessage);
            socket.addEventListener(constants_1.WEB_SOCKET_EVENTS.ERROR, _this.handleError);
            _this.socket = socket;
            return Promise.race([
                new Promise(function (resolve, reject) {
                    setTimeout(function () {
                        reject(constants_1.ERROR_MESSAGES.TIMEOUT);
                    }, _this.openTimeout);
                }),
                new Promise(function (resolve) { return _this.emitter.once(constants_1.WEB_SOCKET_EVENTS.OPEN, resolve); }),
            ]);
        };
        this.handleOpen = function () {
            _this.emit(constants_1.WEB_SOCKET_EVENTS.OPEN);
            logger_1.logger.log(constants_1.MESSAGES.SOCKET_OPENED);
        };
        this.handleClose = function (event) {
            _this.emit(constants_1.WEB_SOCKET_EVENTS.CLOSE, event);
            logger_1.logger.log(constants_1.MESSAGES.SOCKET_CLOSED);
            if (!_this.isDestroyed) {
                _this.reopenWebSocket();
            }
        };
        this.handleMessage = function (event) {
            var message = _this.parseMessage(event);
            if (message) {
                logger_1.logger.log(constants_1.MESSAGES.MESSAGE_RECEIVED);
                _this.emit(constants_1.WEB_SOCKET_EVENTS.MESSAGE, message);
            }
        };
        this.parseMessage = function (event) {
            try {
                return JSON.parse(event.data);
            }
            catch (error) {
                logger_1.logger.warn(constants_1.ERROR_MESSAGES.INVALID_JSON);
                return;
            }
        };
        this.handleError = function (error) {
            _this.emit(constants_1.WEB_SOCKET_EVENTS.ERROR, error);
            logger_1.logger.warn(constants_1.ERROR_MESSAGES.ERROR);
            if (error && error.code === 'ECONNREFUSED') {
                _this.reopenWebSocket();
            }
        };
        this.emit = function (eventName, message) {
            _this.emitter.emit(eventName, message);
        };
        this.emitter = new EventEmitter();
    }
    /**
     * Initializes client
     *
     * @param config
     *
     * @example
     *
     * await blackArrowClient.init({
     *   socketUrl: 'wss://ws.wixmp.com/ws',
     *   tokenUrl: '//vod-meta-test.wix.com/users/get-batoken',
     * })
     *
     * blackArrowClient
     *   .on('open', handler)
     *   .on('message', handler)
     *   .on('error', handler)
     *   .on('close', handler);
     *
     * BlackArrowClient.destroy();
     */
    BlackArrowClient.prototype.init = function (config) {
        var _this = this;
        var socketUrl = config.socketUrl, tokenUrl = config.tokenUrl, tokenHeaders = config.tokenHeaders, _a = config.log, log = _a === void 0 ? false : _a, _b = config.openTimeout, openTimeout = _b === void 0 ? constants_1.OPEN_SOCKET_TIMEOUT : _b, _c = config.reopenTimeout, reopenTimeout = _c === void 0 ? constants_1.REOPEN_TIMEOUT : _c;
        this.openTimeout = openTimeout;
        this.reopenTimeout = reopenTimeout;
        this.isDestroyed = false;
        this.retries = 2;
        logger_1.logger.isActive = log;
        if (this.initializationPromise) {
            return this.initializationPromise;
        }
        token_1.token.init({ url: tokenUrl, headers: tokenHeaders });
        this.socketUrl = socketUrl;
        this.initializationPromise = token_1.token
            .get()
            .then(function (tokenValue) { return (_this.token = tokenValue); })
            .then(this.openWebSocket);
        return this.initializationPromise;
    };
    /**
     * Removes all listeners, closes socket if opened.
     * Deletes last used token.
     */
    BlackArrowClient.prototype.destroy = function () {
        token_1.token.destroy();
        clearTimeout(this.reopenTimer);
        if (this.socket) {
            this.isDestroyed = true;
            this.socket.close();
            this.emitter.emit(constants_1.WEB_SOCKET_EVENTS.CLOSE);
            this.removeSocketListeners();
        }
        this.emitter.removeAllListeners();
        delete this.token;
        delete this.initializationPromise;
    };
    BlackArrowClient.prototype.removeSocketListeners = function () {
        this.socket.removeEventListener(constants_1.WEB_SOCKET_EVENTS.OPEN, this.handleOpen);
        this.socket.removeEventListener(constants_1.WEB_SOCKET_EVENTS.MESSAGE, this.handleMessage);
        this.socket.removeEventListener(constants_1.WEB_SOCKET_EVENTS.ERROR, this.handleError);
        this.socket.removeEventListener(constants_1.WEB_SOCKET_EVENTS.CLOSE, this.handleClose);
    };
    BlackArrowClient.prototype.reopenWebSocket = function () {
        var _this = this;
        if (this.retries < 0) {
            return;
        }
        this.removeSocketListeners();
        clearTimeout(this.reopenTimer);
        this.reopenTimer = setTimeout(function () {
            logger_1.logger.log(constants_1.MESSAGES.REOPENING);
            token_1.token
                .get()
                .then(_this.openWebSocket)
                .catch(function () { });
        }, this.reopenTimeout);
        this.retries--;
    };
    BlackArrowClient.prototype.getWebSocketUrl = function () {
        return this.socketUrl + "?token=" + this.token;
    };
    return BlackArrowClient;
}());
exports.BlackArrowClient = BlackArrowClient;
//# sourceMappingURL=black-arrow-client.js.map